import React, { useEffect, useRef, useState } from 'react';
import { createChart, ColorType, CandlestickData, Time, CandlestickSeries, HistogramSeries, LineSeries, ISeriesApi, LineData, MouseEventParams } from 'lightweight-charts';
import { CandleData, TechnicalIndicatorData } from '../../api/metrics';
import { TimezoneMode, loadTimezone, saveTimezone, formatCrosshairLabel, formatTickMarkLabel, formatLegendLabel } from '../../utils/timezone';
import { getDefaultVisibleCandles } from '../../utils/chartZoom';
import { isIchimokuAvailable } from '../../utils/granularityMetricConfig';
import { applyIchimokuDisplacement, IchimokuMetricType, DisplacedPoint } from '../../utils/ichimokuDisplacement';
import { ichimokuPalette } from './ichimokuPalette';
import { IchimokuCloudPrimitive } from './IchimokuCloudPrimitive';
import TimezoneControl from '../shared/TimezoneControl';

interface MovingAverageConfig {
  type: 'SMA' | 'EMA';
  period: number;
  color: string;
  enabled: boolean;
}

interface CandlestickChartProps {
  data: CandleData[];
  loading?: boolean;
  granularityMins?: number;
  indicators?: TechnicalIndicatorData[];
  movingAverages?: MovingAverageConfig[];
  bollingerEnabled?: boolean;
  vwapEnabled?: boolean;
  rsiEnabled?: boolean;
  volumeSmaEnabled?: boolean;
  volumeEmaEnabled?: boolean;
  ichimokuEnabled?: boolean;
  productId?: string;
  timezone?: TimezoneMode;
  onTimezoneChange?: (tz: TimezoneMode) => void;
}

interface CandleLegendData {
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

const CandlestickChart: React.FC<CandlestickChartProps> = ({
  data, 
  loading, 
  granularityMins = 60,
  indicators = [],
  movingAverages = [],
  bollingerEnabled = false,
  vwapEnabled = false,
  rsiEnabled = false,
  volumeSmaEnabled = false,
  volumeEmaEnabled = false,
  ichimokuEnabled = false,
  productId,
  timezone: timezoneProp,
  onTimezoneChange,
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const candleSeriesRef = useRef<any>(null);
  const volumeSeriesRef = useRef<any>(null);
  const maSeriesRefs = useRef<Map<string, ISeriesApi<"Line">>>(new Map());
  const bollingerSeriesRefs = useRef<Map<string, ISeriesApi<"Line">>>(new Map());
  const vwapSeriesRefs = useRef<Map<string, ISeriesApi<"Line">>>(new Map());
  const volumeMaSeriesRefs = useRef<Map<string, ISeriesApi<"Line">>>(new Map());
  const ichimokuSeriesRefs = useRef<Map<string, ISeriesApi<"Line">>>(new Map());
  const ichimokuCloudPrimitiveRef = useRef<IchimokuCloudPrimitive | null>(null);

  // Use prop if provided, otherwise fall back to local state
  const [localTimezone, setLocalTimezone] = useState<TimezoneMode>(loadTimezone());
  const timezone = timezoneProp ?? localTimezone;

  const [legendData, setLegendData] = useState<CandleLegendData | null>(null);
  const lastLegendUnixSecRef = useRef<number | null>(null);


  // Initialize chart and load data
  useEffect(() => {
    if (!chartContainerRef.current || loading || !data || data.length === 0) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#94a3b8',
      },
      grid: {
        vertLines: { color: 'rgba(148, 163, 184, 0.1)' },
        horzLines: { color: 'rgba(148, 163, 184, 0.2)' },
      },
      crosshair: {
        mode: 0,
        vertLine: {
          width: 1,
          color: '#8B5CF6',
          style: 1,
          labelBackgroundColor: '#8B5CF6',
        },
        horzLine: {
          width: 1,
          color: '#8B5CF6',
          style: 1,
          labelBackgroundColor: '#8B5CF6',
        },
      },
      timeScale: {
        borderColor: '#475569',
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (time: Time, tickMarkType: number) =>
          formatTickMarkLabel(time as number, tickMarkType, timezone),
      },
      localization: {
        timeFormatter: (t: number) => formatCrosshairLabel(t, timezone),
        priceFormatter: (price: number) => {
          return productId === 'ADA-USDC' ? price.toFixed(4) : price.toFixed(2);
        },
      },
      rightPriceScale: {
        borderColor: '#475569',
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
    });

    chartRef.current = chart;


    // Add candlestick series
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#10b981',
      downColor: '#ef4444',
      borderVisible: false,
      wickUpColor: '#10b981',
      wickDownColor: '#ef4444',
      priceLineVisible: false, // hide dotted current price line across chart
      lastValueVisible: true,
    });

    candleSeriesRef.current = candleSeries;

    // Add volume histogram series
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: {
        type: 'volume',
      },
      priceScaleId: '',
      priceLineVisible: false, // hide dotted current price line across chart
      lastValueVisible: true,
    });

    volumeSeriesRef.current = volumeSeries;

    // Set volume scale to 25% of chart height at bottom
    chart.priceScale('').applyOptions({
      scaleMargins: {
        top: 0.75,
        bottom: 0,
      },
    });

    // Convert and set candle data
    const candleData: CandlestickData<Time>[] = data.map(candle => {
      const timestamp = new Date(candle.timestamp).getTime() / 1000;
      return {
        time: timestamp as Time,
        open: Number(candle.open),
        high: Number(candle.high),
        low: Number(candle.low),
        close: Number(candle.close),
      };
    });

    // Convert and set volume data
    const volumeData = data.map(candle => {
      const timestamp = new Date(candle.timestamp).getTime() / 1000;
      const close = Number(candle.close);
      const open = Number(candle.open);
      return {
        time: timestamp as Time,
        value: Number(candle.volume),
        color: close >= open ? 'rgba(16, 185, 129, 0.5)' : 'rgba(239, 68, 68, 0.5)',
      };
    });

    // Set data on series
    candleSeries.setData(candleData);
    volumeSeries.setData(volumeData);

    // Initialize legend with most recent candle
    if (data.length > 0) {
      const mostRecentCandle = data[data.length - 1];
      const timestamp = new Date(mostRecentCandle.timestamp);
      const timeString = formatLegendLabel(Math.floor(timestamp.getTime() / 1000), timezone);

      setLegendData({
        time: timeString,
        open: Number(mostRecentCandle.open),
        high: Number(mostRecentCandle.high),
        low: Number(mostRecentCandle.low),
        close: Number(mostRecentCandle.close),
        volume: Number(mostRecentCandle.volume),
      });
      lastLegendUnixSecRef.current = Math.floor(timestamp.getTime() / 1000);
    }

    // Subscribe to crosshair move to update legend
    chart.subscribeCrosshairMove((param: MouseEventParams<Time>) => {
      // If no hover, show most recent candle (don't hide legend)
      if (!param.time || !param.seriesData.get(candleSeries)) {
        if (data.length > 0) {
          const mostRecentCandle = data[data.length - 1];
          const timestamp = new Date(mostRecentCandle.timestamp);
          const timeString = formatLegendLabel(Math.floor(timestamp.getTime() / 1000), timezone);

          setLegendData({
            time: timeString,
            open: Number(mostRecentCandle.open),
            high: Number(mostRecentCandle.high),
            low: Number(mostRecentCandle.low),
            close: Number(mostRecentCandle.close),
            volume: Number(mostRecentCandle.volume),
          });
          lastLegendUnixSecRef.current = Math.floor(timestamp.getTime() / 1000);
        }
        return;
      }

      const candleDataPoint = param.seriesData.get(candleSeries) as any;
      const volumeDataPoint = param.seriesData.get(volumeSeries) as any;

      if (candleDataPoint) {
        // Format time based on timezone
        const timeString = formatLegendLabel(param.time as number, timezone);

        setLegendData({
          time: timeString,
          open: candleDataPoint.open,
          high: candleDataPoint.high,
          low: candleDataPoint.low,
          close: candleDataPoint.close,
          volume: volumeDataPoint?.value || 0,
        });
        lastLegendUnixSecRef.current = param.time as number;
      }
    });

    // Set visible range based on granularity (instead of showing ALL data)
    const defaultVisibleCandles = getDefaultVisibleCandles(granularityMins);
    const totalCandles = candleData.length;

    if (totalCandles > 0) {
      // Show the most recent N candles (where N is based on granularity)
      const from = Math.max(0, totalCandles - defaultVisibleCandles);
      const to = totalCandles - 1;

      chart.timeScale().setVisibleLogicalRange({
        from: from,
        to: to,
      });
    }

    // Add moving average lines if indicators are provided
    if (indicators.length > 0 && movingAverages.length > 0) {
      // Clear existing MA series
      maSeriesRefs.current.forEach((series) => {
        chart.removeSeries(series);
      });
      maSeriesRefs.current.clear();

      // Create MA series for each enabled moving average
      movingAverages.forEach((maConfig) => {
        if (!maConfig.enabled) return;

        // Filter indicators for this specific MA
        const maData = indicators
          .filter(ind => 
            ind.metricType === maConfig.type && 
            ind.period === maConfig.period &&
            ind.complete
          )
          .map(ind => ({
            time: (new Date(ind.metricTime).getTime() / 1000) as Time,
            value: Number(ind.value) // Ensure numeric value for Lightweight Charts
          }))
          .sort((a, b) => (a.time as number) - (b.time as number));

        if (maData.length > 0) {
          const maSeries = chart.addSeries(LineSeries, {
            color: maConfig.color,
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: true,
          });

          maSeries.setData(maData as LineData[]);
          maSeriesRefs.current.set(`${maConfig.type}_${maConfig.period}`, maSeries);
        }
      });
    }

    // Handle Bollinger Bands
    // Always clear existing Bollinger series first
    bollingerSeriesRefs.current.forEach((series) => {
      chart.removeSeries(series);
    });
    bollingerSeriesRefs.current.clear();

    // Add Bollinger Bands if enabled
    if (indicators.length > 0 && bollingerEnabled) {
      // Get Bollinger Bands data (period 20)
      const bbUpper = indicators
        .filter(ind => ind.metricType === 'BOLLINGER_UPPER' && ind.period === 20 && ind.complete)
        .map(ind => ({
          time: (new Date(ind.metricTime).getTime() / 1000) as Time,
          value: Number(ind.value)
        }))
        .sort((a, b) => (a.time as number) - (b.time as number));

      const bbMiddle = indicators
        .filter(ind => ind.metricType === 'BOLLINGER_MIDDLE' && ind.period === 20 && ind.complete)
        .map(ind => ({
          time: (new Date(ind.metricTime).getTime() / 1000) as Time,
          value: Number(ind.value)
        }))
        .sort((a, b) => (a.time as number) - (b.time as number));

      const bbLower = indicators
        .filter(ind => ind.metricType === 'BOLLINGER_LOWER' && ind.period === 20 && ind.complete)
        .map(ind => ({
          time: (new Date(ind.metricTime).getTime() / 1000) as Time,
          value: Number(ind.value)
        }))
        .sort((a, b) => (a.time as number) - (b.time as number));

      if (bbUpper.length > 0) {
        const upperSeries = chart.addSeries(LineSeries, {
          color: '#00D9FF',
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: true,
        });
        upperSeries.setData(bbUpper as LineData[]);
        bollingerSeriesRefs.current.set('BB_UPPER', upperSeries);
      }

      if (bbMiddle.length > 0) {
        const middleSeries = chart.addSeries(LineSeries, {
          color: '#FFD700',
          lineWidth: 2,
          lineStyle: 2,
          priceLineVisible: false,
          lastValueVisible: true,
        });
        middleSeries.setData(bbMiddle as LineData[]);
        bollingerSeriesRefs.current.set('BB_MIDDLE', middleSeries);
      }

      if (bbLower.length > 0) {
        const lowerSeries = chart.addSeries(LineSeries, {
          color: '#00D9FF',
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: true,
        });
        lowerSeries.setData(bbLower as LineData[]);
        bollingerSeriesRefs.current.set('BB_LOWER', lowerSeries);
      }
    }

    // Handle VWAP Bands
    // Always clear existing VWAP series first
    vwapSeriesRefs.current.forEach((series) => {
      chart.removeSeries(series);
    });
    vwapSeriesRefs.current.clear();

    // Add VWAP Bands if enabled
    if (indicators.length > 0 && vwapEnabled) {
      // Get VWAP Bands data (period 20)
      const vwapUpper = indicators
        .filter(ind => ind.metricType === 'VWAP_UPPER' && ind.period === 20 && ind.complete)
        .map(ind => ({
          time: (new Date(ind.metricTime).getTime() / 1000) as Time,
          value: Number(ind.value)
        }))
        .sort((a, b) => (a.time as number) - (b.time as number));

      const vwapMiddle = indicators
        .filter(ind => ind.metricType === 'VWAP' && ind.period === 20 && ind.complete)
        .map(ind => ({
          time: (new Date(ind.metricTime).getTime() / 1000) as Time,
          value: Number(ind.value)
        }))
        .sort((a, b) => (a.time as number) - (b.time as number));

      const vwapLower = indicators
        .filter(ind => ind.metricType === 'VWAP_LOWER' && ind.period === 20 && ind.complete)
        .map(ind => ({
          time: (new Date(ind.metricTime).getTime() / 1000) as Time,
          value: Number(ind.value)
        }))
        .sort((a, b) => (a.time as number) - (b.time as number));

      if (vwapUpper.length > 0) {
        const upperSeries = chart.addSeries(LineSeries, {
          color: '#5b9bd5',
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: true,
        });
        upperSeries.setData(vwapUpper as LineData[]);
        vwapSeriesRefs.current.set('VWAP_UPPER', upperSeries);
      }

      if (vwapMiddle.length > 0) {
        const middleSeries = chart.addSeries(LineSeries, {
          color: '#4682b4',
          lineWidth: 2,
          lineStyle: 2,
          priceLineVisible: false,
          lastValueVisible: true,
        });
        middleSeries.setData(vwapMiddle as LineData[]);
        vwapSeriesRefs.current.set('VWAP', middleSeries);
      }

      if (vwapLower.length > 0) {
        const lowerSeries = chart.addSeries(LineSeries, {
          color: '#5b9bd5',
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: true,
        });
        lowerSeries.setData(vwapLower as LineData[]);
        vwapSeriesRefs.current.set('VWAP_LOWER', lowerSeries);
      }
    }

    // Ichimoku series + cloud primitive are managed in a dedicated effect below
    // (PLAN-AMEND-001 / TASK-017) so toggling Ichimoku does not recreate the chart.

    // Handle Volume Moving Averages (render on volume scale)
    // Always clear existing volume MA series first
    volumeMaSeriesRefs.current.forEach((series) => {
      chart.removeSeries(series);
    });
    volumeMaSeriesRefs.current.clear();

    // Add Volume SMA if enabled
    if (indicators.length > 0 && volumeSmaEnabled) {
      const volumeSmaData = indicators
        .filter(ind => ind.metricType === 'VOLUME_SMA' && ind.period === 20 && ind.complete)
        .map(ind => ({
          time: (new Date(ind.metricTime).getTime() / 1000) as Time,
          value: Number(ind.value)
        }))
        .sort((a, b) => (a.time as number) - (b.time as number));

      if (volumeSmaData.length > 0) {
        const volumeSmaSeries = chart.addSeries(LineSeries, {
          color: '#4CAF50',
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: true,
          priceScaleId: '', // Use volume scale
        });
        volumeSmaSeries.setData(volumeSmaData as LineData[]);
        volumeMaSeriesRefs.current.set('VOLUME_SMA', volumeSmaSeries);
      }
    }

    // Add Volume EMA if enabled
    if (indicators.length > 0 && volumeEmaEnabled) {
      const volumeEmaData = indicators
        .filter(ind => ind.metricType === 'VOLUME_EMA' && ind.period === 20 && ind.complete)
        .map(ind => ({
          time: (new Date(ind.metricTime).getTime() / 1000) as Time,
          value: Number(ind.value)
        }))
        .sort((a, b) => (a.time as number) - (b.time as number));

      if (volumeEmaData.length > 0) {
        const volumeEmaSeries = chart.addSeries(LineSeries, {
          color: '#FF9800',
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: true,
          priceScaleId: '', // Use volume scale
        });
        volumeEmaSeries.setData(volumeEmaData as LineData[]);
        volumeMaSeriesRefs.current.set('VOLUME_EMA', volumeEmaSeries);
      }
    }

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
      maSeriesRefs.current.clear();
      bollingerSeriesRefs.current.clear();
      vwapSeriesRefs.current.clear();
      volumeMaSeriesRefs.current.clear();
      ichimokuSeriesRefs.current.clear();
      ichimokuCloudPrimitiveRef.current = null;
    };
  // Removed `timezone` from deps; we update via applyOptions in a separate effect.
  // Removed `ichimokuEnabled` from deps (PLAN-AMEND-001): Ichimoku series are
  // managed in a dedicated effect so toggling does not recreate the chart.
  }, [loading, data, indicators, movingAverages, bollingerEnabled, vwapEnabled, rsiEnabled, volumeSmaEnabled, volumeEmaEnabled, granularityMins, productId]);

  // Ichimoku series management — runs imperatively against the existing chart
  // instance. Crucially does NOT live in the chart-instance effect (PLAN-AMEND-001):
  // the chart is created once and Ichimoku series are added/removed in-place, so
  // toggling Ichimoku does not blow away the chart and the operator's visible
  // range survives the toggle.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    // Detach the existing cloud primitive (if any) before removing its host series.
    const prevSpanA = ichimokuSeriesRefs.current.get('SENKOU_SPAN_A');
    if (prevSpanA && ichimokuCloudPrimitiveRef.current) {
      try {
        prevSpanA.detachPrimitive(ichimokuCloudPrimitiveRef.current);
      } catch {
        // primitive may already be detached if the series was removed earlier
      }
    }
    ichimokuCloudPrimitiveRef.current = null;
    ichimokuSeriesRefs.current.forEach((series) => {
      chart.removeSeries(series);
    });
    ichimokuSeriesRefs.current.clear();

    const ichimokuActive = ichimokuEnabled && isIchimokuAvailable(granularityMins);
    if (!ichimokuActive || indicators.length === 0) return;

    const buildPoints = (metricType: IchimokuMetricType, period: number): DisplacedPoint[] => {
      const rows = indicators
        .filter((ind) => ind.metricType === metricType && ind.period === period && ind.complete)
        .sort((a, b) => new Date(a.metricTime).getTime() - new Date(b.metricTime).getTime());
      return applyIchimokuDisplacement(rows, metricType, granularityMins);
    };

    const tenkan = buildPoints('TENKAN_SEN', 9);
    if (tenkan.length > 0) {
      const series = chart.addSeries(LineSeries, {
        color: ichimokuPalette.tenkan,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: true,
      });
      series.setData(tenkan as LineData[]);
      ichimokuSeriesRefs.current.set('TENKAN_SEN', series);
    }

    const kijun = buildPoints('KIJUN_SEN', 26);
    if (kijun.length > 0) {
      const series = chart.addSeries(LineSeries, {
        color: ichimokuPalette.kijun,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: true,
      });
      series.setData(kijun as LineData[]);
      ichimokuSeriesRefs.current.set('KIJUN_SEN', series);
    }

    const chikou = buildPoints('CHIKOU_SPAN', 26);
    if (chikou.length > 0) {
      const series = chart.addSeries(LineSeries, {
        color: ichimokuPalette.chikou,
        lineWidth: 1,
        lineStyle: 2,
        priceLineVisible: false,
        lastValueVisible: true,
      });
      series.setData(chikou as LineData[]);
      ichimokuSeriesRefs.current.set('CHIKOU_SPAN', series);
    }

    const spanA = buildPoints('SENKOU_SPAN_A', 26);
    const spanB = buildPoints('SENKOU_SPAN_B', 52);
    if (spanA.length > 0 && spanB.length > 0) {
      const spanASeries = chart.addSeries(LineSeries, {
        color: ichimokuPalette.spanA,
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false,
      });
      spanASeries.setData(spanA as LineData[]);
      ichimokuSeriesRefs.current.set('SENKOU_SPAN_A', spanASeries);

      const spanBSeries = chart.addSeries(LineSeries, {
        color: ichimokuPalette.spanB,
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false,
      });
      spanBSeries.setData(spanB as LineData[]);
      ichimokuSeriesRefs.current.set('SENKOU_SPAN_B', spanBSeries);

      const cloud = new IchimokuCloudPrimitive();
      cloud.setSeries(spanASeries, spanBSeries);
      spanASeries.attachPrimitive(cloud);
      cloud.setData(spanA, spanB);
      ichimokuCloudPrimitiveRef.current = cloud;
    }
  }, [ichimokuEnabled, indicators, granularityMins]);

  // Apply timezone-specific formatting instantly without recreating the chart
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.applyOptions({
      localization: {
        timeFormatter: (t: number) => formatCrosshairLabel(t, timezone),
      },
      timeScale: {
        tickMarkFormatter: (time: Time, tickMarkType: number) =>
          formatTickMarkLabel(time as number, tickMarkType, timezone),
      },
    });

    // Also update legend time string immediately to reflect new timezone
    const unixSec = lastLegendUnixSecRef.current ?? (data && data.length > 0 ? Math.floor(new Date(data[data.length - 1].timestamp).getTime() / 1000) : null);
    if (unixSec && legendData) {
      setLegendData({ ...legendData, time: formatLegendLabel(unixSec, timezone) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezone, data]);

  const handleTimezoneChange = (newTimezone: TimezoneMode) => {
    if (onTimezoneChange) {
      onTimezoneChange(newTimezone);
    } else {
      setLocalTimezone(newTimezone);
      saveTimezone(newTimezone);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        height: 500, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#94a3b8'
      }}>
        Loading chart data...
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{ 
        height: 500, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#94a3b8'
      }}>
        No candle data available
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* OHLC Legend - always visible, shows most recent or hovered candle */}
      <div style={{
        display: 'flex',
        gap: '16px',
        padding: '8px 12px',
        fontSize: '13px',
        fontFamily: 'monospace',
        color: '#e2e8f0',
        backgroundColor: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '4px',
        marginBottom: '8px',
        flexShrink: 0,
        border: '1px solid rgba(139, 92, 246, 0.2)'
      }}>
        {legendData ? (
          <>
            <span style={{ color: '#94a3b8' }}>
              O <span style={{ color: '#e2e8f0', fontWeight: 600 }}>${legendData.open.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</span>
            </span>
            <span style={{ color: '#94a3b8' }}>
              H <span style={{ color: '#10b981', fontWeight: 600 }}>${legendData.high.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</span>
            </span>
            <span style={{ color: '#94a3b8' }}>
              L <span style={{ color: '#ef4444', fontWeight: 600 }}>${legendData.low.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</span>
            </span>
            <span style={{ color: '#94a3b8' }}>
              C <span style={{ color: legendData.close >= legendData.open ? '#10b981' : '#ef4444', fontWeight: 600 }}>${legendData.close.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</span>
            </span>
            <span style={{ color: '#94a3b8' }}>
              Vol <span style={{ color: '#8B5CF6', fontWeight: 600 }}>{legendData.volume.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
            </span>
            <span style={{ color: '#64748b', fontSize: '11px', marginLeft: 'auto' }}>
              {legendData.time}
            </span>
          </>
        ) : (
          <span style={{ color: '#64748b' }}>Loading...</span>
        )}
      </div>

      {/* Timezone Control */}
      <TimezoneControl timezone={timezone} onTimezoneChange={handleTimezoneChange} />

      {/* Chart Container - TradingView Lightweight Charts handles everything */}
      <div 
        ref={chartContainerRef}
        style={{ 
          width: '100%', 
          flex: 1,
          minHeight: 0,
          position: 'relative'
        }}
      />
    </div>
  );
};

export default CandlestickChart;
